import { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import api, { imgSrc } from "@/lib/api";
import { ScrollTrigger } from "@/lib/gsap";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import useLenis from "@/components/cinematic/useLenis";
import CustomCursor from "@/components/cinematic/CustomCursor";
import HeroSequence from "@/components/cinematic/HeroSequence";
import HorizontalGallery from "@/components/cinematic/HorizontalGallery";
import TypographyTakeover from "@/components/cinematic/TypographyTakeover";
import ReadyHeadline from "@/components/cinematic/ReadyHeadline";
import PackagesSection from "@/components/home/PackagesSection";
import AboutSection from "@/components/home/AboutSection";
import InstagramSection from "@/components/home/InstagramSection";
import FaqSection from "@/components/home/FaqSection";
import ContactSection from "@/components/home/ContactSection";

const PROJECTS = ["Carnival", "Junkanoo", "Brand", "Events", "Sports", "Lifestyle", "Creative"];

export default function Home() {
  const [images, setImages] = useState([]);
  const [settings, setSettings] = useState({});
  const location = useLocation();
  useLenis();

  useEffect(() => {
    api.get("/portfolio/images").then((r) => setImages(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => { ScrollTrigger.refresh(); document.getElementById(id)?.scrollIntoView(); }, 400);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  useEffect(() => {
    if (!images.length) return undefined;
    const refresh = () => ScrollTrigger.refresh();
    const timers = [setTimeout(refresh, 300), setTimeout(refresh, 900), setTimeout(refresh, 1800)];
    window.addEventListener("load", refresh);
    return () => { timers.forEach(clearTimeout); window.removeEventListener("load", refresh); };
  }, [images]);

  const data = useMemo(() => {
    const byCat = (c) => images.filter((i) => i.category?.toLowerCase() === c.toLowerCase()).map(imgSrc);
    const all = images.map(imgSrc);
    const pick = (c, n = 0) => byCat(c)[n] || all[n % (all.length || 1)];
    const pickLand = (c) => {
      const arr = images.filter((i) => i.category?.toLowerCase() === c.toLowerCase() && i.orientation === "landscape");
      return arr[0] ? imgSrc(arr[0]) : pick(c);
    };
    const projects = PROJECTS.map((c) => ({ title: c.toUpperCase(), url: pick(c) }));
    const heroPhotos = [pick("Sports"), pick("Lifestyle"), pick("Brand")];
    // interleave categories round-robin so the gallery shows variety, not only Carnival
    const cats = ["Carnival", "Junkanoo", "Events", "Brand", "Sports", "Lifestyle", "Creative"].map(byCat);
    const galleryImgs = [];
    const maxLen = Math.max(0, ...cats.map((a) => a.length));
    for (let i = 0; i < maxLen; i += 1) cats.forEach((arr) => { if (arr[i]) galleryImgs.push(arr[i]); });
    return { projects, heroPhotos, galleryImgs: galleryImgs.length ? galleryImgs : all, ttImage: pickLand("Junkanoo") };
  }, [images]);

  return (
    <div className="bg-[#050708] min-h-screen">
      <CustomCursor />
      <Header />
      <main className="overflow-x-hidden">
        {images.length > 0 && (
          <>
            <HeroSequence photos={data.heroPhotos} projects={data.projects} />
            <HorizontalGallery images={data.galleryImgs} />
            <TypographyTakeover image={data.ttImage} />
          </>
        )}
        <ReadyHeadline />
        <PackagesSection />
        <AboutSection settings={settings} />
        <InstagramSection settings={settings} />
        <FaqSection />
        <ContactSection />
      </main>
      <Footer settings={settings} />
    </div>
  );
}
